import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import {
  FaPlus,
  FaCheckCircle,
  FaClock,
  FaSpinner,
  FaStore,
  FaBox,
  FaTrash,
  FaSync,
  FaGripVertical,
  FaRocket
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../core/api/client';
import './Sashe.css';

function NextRunCountdown() {
  const [nextRun, setNextRun] = useState(null);
  const countdownRef = useRef(null);

  useEffect(() => {
    api.get('/sashe/next-run').then(response => {
      setNextRun(new Date(response.data.next_run));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!nextRun) return;
    const update = () => {
      const diff = nextRun - new Date();
      if (diff <= 0) {
        if (countdownRef.current) countdownRef.current.textContent = 'Prebieha...';
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      if (countdownRef.current) {
        countdownRef.current.textContent = `${h}h ${m.toString().padStart(2, '0')}m ${s.toString().padStart(2, '0')}s`;
      }
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [nextRun]);

  if (!nextRun) return null;

  return (
    <div className="next-run-box">
      <FaRocket className="next-run-icon" />
      <div className="next-run-info">
        <span className="next-run-label">Nasledujuce spustenie</span>
        <span className="next-run-time">
          {nextRun.toLocaleString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      <div className="next-run-countdown" ref={countdownRef}></div>
    </div>
  );
}

function Sashe() {
  const [monamaProducts, setMonamaProducts] = useState([]);
  const [svadobkaProducts, setSvadobkaProducts] = useState([]);
  const [stats, setStats] = useState({
    monama: { waiting: 0, completed: 0 },
    monama_svadobka: { waiting: 0, completed: 0 }
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    product_name: '',
    eshop: 'monama'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
    fetchStats();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/sashe/products');
      const products = response.data.products || response.data;
      
      // Sort function - waiting/processing first, then completed at bottom
      const sortProducts = (productList) => {
        return [...productList].sort((a, b) => {
          // First sort by status (waiting/processing before completed)
          if (a.status !== 'pouzite' && b.status === 'pouzite') return -1;
          if (a.status === 'pouzite' && b.status !== 'pouzite') return 1;
          // Then by order_index
          return (a.order_index || 0) - (b.order_index || 0);
        });
      };
      
      // Rozdeliť produkty podľa e-shopu a zoradiť
      const monamaFiltered = products.filter(p => p.eshop === 'monama');
      const svadobkaFiltered = products.filter(p => p.eshop === 'monama_svadobka');
      
      setMonamaProducts(sortProducts(monamaFiltered));
      setSvadobkaProducts(sortProducts(svadobkaFiltered));
    } catch (error) {
      toast.error('Chyba pri načítaní produktov');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/sashe/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/sashe/products', formData);
      toast.success('Produkt pridaný');
      setFormData(prev => ({ product_name: '', eshop: prev.eshop }));
      setShowAddForm(false);
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error('Chyba pri pridávaní produktu');
    }
  };

  const handleDelete = async (productId, eshop) => {
    if (window.confirm('Naozaj chcete zmazať tento produkt?')) {
      try {
        await api.delete(`/sashe/products/${productId}`);
        toast.success('Produkt zmazaný');
        fetchProducts();
        fetchStats();
      } catch (error) {
        toast.error('Chyba pri mazaní produktu');
      }
    }
  };

  const handleRefresh = async (eshop) => {
    try {
      // Reset status pre produkty daného e-shopu
      await api.post(`/sashe/refresh/${eshop}`);
      toast.success(`${eshop === 'monama' ? 'Monama' : 'Svadobka'} produkty obnovené`);
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error('Chyba pri obnovovaní');
    }
  };

  const handleRefreshSingle = async (productId) => {
    try {
      const response = await api.post(`/sashe/refresh-single/${productId}`);
      toast.success('Produkt obnovený');
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error('Chyba pri obnovovaní produktu');
    }
  };

  const handleReorder = async (newOrder, eshop) => {
    // Sort to keep completed at bottom
    const sortedOrder = [...newOrder].sort((a, b) => {
      if (a.status !== 'pouzite' && b.status === 'pouzite') return -1;
      if (a.status === 'pouzite' && b.status !== 'pouzite') return 1;
      return 0;
    });
    
    // Update local state immediately for smooth UX
    if (eshop === 'monama') {
      setMonamaProducts(sortedOrder);
    } else {
      setSvadobkaProducts(sortedOrder);
    }
    
    // Update order on backend
    try {
      const orderData = sortedOrder.map((product, index) => ({
        id: product.id,
        order_index: index
      }));
      await api.post('/sashe/reorder', { eshop, order: orderData });
    } catch (error) {
      toast.error('Chyba pri zmene poradia');
      fetchProducts(); // Reload on error
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'aktivne': return <FaClock className="status-icon waiting" />;
      case 'processing': return <FaSpinner className="status-icon processing spinning" />;
      case 'pouzite': return <FaCheckCircle className="status-icon completed" />;
      default: return null;
    }
  };

  const ProductCard = ({ product, eshop }) => (
    <motion.div
      className="product-card"
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ scale: 1.02 }}
    >
      <div className="product-card-header">
        <div className="drag-handle">
          <FaGripVertical />
        </div>
        <div className="product-status">
          {getStatusIcon(product.status)}
        </div>
        <div className="product-actions">
          {product.status === 'pouzite' && (
            <button
              className="refresh-single-btn"
              onClick={() => handleRefreshSingle(product.id)}
              title="Obnoviť produkt"
            >
              <FaSync />
            </button>
          )}
          <button
            className="delete-btn"
            onClick={() => handleDelete(product.id, eshop)}
            title="Zmazať produkt"
          >
            <FaTrash />
          </button>
        </div>
      </div>
      <div className="product-card-body">
        <h4>{product.product_name}</h4>
        <div className="product-meta">
          <span className="date">
            Vytvorené: {new Date(product.created_at).toLocaleDateString('sk-SK')}
          </span>
          {product.processed_at && (
            <span className="date">
              Spracované: {new Date(product.processed_at).toLocaleDateString('sk-SK')}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );

  const ShopColumn = ({ title, products, eshop, color }) => {
    return (
      <div className="shop-column">
        <div className="column-header" style={{ borderColor: color }}>
          <div className="column-title">
            <FaStore style={{ color }} />
            <h2>{title}</h2>
          </div>
          <div className="column-stats">
            <span className="stat">
              <FaClock /> {stats[eshop].waiting}
            </span>
            <span className="stat">
              <FaCheckCircle /> {stats[eshop].completed}
            </span>
            <button
              className="refresh-btn"
              onClick={() => handleRefresh(eshop)}
              title="Obnoviť produkty"
            >
              <FaSync />
            </button>
          </div>
        </div>
        
        <div className="products-list">
        {products.length === 0 ? (
          <div className="empty-column">
            <FaBox className="empty-icon" />
            <p>Žiadne produkty</p>
          </div>
        ) : (
          <Reorder.Group
            axis="y"
            values={products}
            onReorder={(newOrder) => handleReorder(newOrder, eshop)}
          >
            {products.map((product) => (
              <Reorder.Item key={product.id} value={product}>
                <ProductCard product={product} eshop={eshop} />
              </Reorder.Item>
            ))}
          </Reorder.Group>
        )}
      </div>
    </div>
    );
  };

  return (
    <div className="sashe-page">
      <div className="page-header">
        <h1>Sashe-klikač</h1>
        <div className="header-actions">
          <motion.button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaPlus /> Pridať produkt
          </motion.button>
        </div>
      </div>

      <AnimatePresence>
        {showAddForm && (
          <motion.div 
            className="add-form-container"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <form onSubmit={handleSubmit} className="product-form">
              <div className="form-grid">
                <div className="input-group">
                  <label>Názov produktu</label>
                  <input
                    type="text"
                    value={formData.product_name}
                    onChange={(e) => setFormData({...formData, product_name: e.target.value})}
                    required
                    placeholder='Napr: Darček pre učiteľku "Motýle"'
                  />
                </div>
                <div className="input-group">
                  <label>E-shop</label>
                  <select
                    value={formData.eshop}
                    onChange={(e) => setFormData({...formData, eshop: e.target.value})}
                  >
                    <option value="monama">Monamadesign</option>
                    <option value="monama_svadobka">Svadobka</option>
                  </select>
                </div>
              </div>
              <div className="form-buttons">
                <button type="submit" className="btn btn-primary">
                  Pridať produkt
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowAddForm(false)}
                >
                  Zrušiť
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <NextRunCountdown />

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="columns-container">
          <ShopColumn
            title="Monamadesign"
            products={monamaProducts}
            eshop="monama"
            color="#3b82f6"
          />
          <ShopColumn
            title="Svadobka"
            products={svadobkaProducts}
            eshop="monama_svadobka"
            color="#8b5cf6"
          />
        </div>
      )}

      <div className="info-box">
        <h3>Informacie</h3>
        <p>
          <strong>Automaticke spustanie:</strong> Skript sa spusta automaticky kazdy den cez CRON. Cas sa posunie o 5 minut kazdy den.
        </p>
        <p>
          Pri kazdom spusteni sa spracuje 1 produkt pre kazdy e-shop (max 2 produkty).
        </p>
        <p>
          <strong>Tip:</strong> Produkty mozete presuvatat tahanim za ikonu <FaGripVertical /> pre zmenu poradia spracovania.
        </p>
      </div>
    </div>
  );
}

export default Sashe;